import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { AdminUsersService } from '../../../openapi'
import { apiCall } from '../../core'

// Types for device data
interface DeviceDetail {
  deviceId: string
  deviceType: string
  deviceName?: string
  userAgent?: string
  ipAddress?: string
  lastActiveAt: string
  createdAt: string
  revoked: boolean
  country?: string
  countryCode?: string
  countryFlag?: string
  city?: string
  region?: string
}

interface DevicesState {
  userDevices: Record<string, DeviceDetail[]>
  loading: Record<string, boolean>
  fetched: Record<string, boolean>
  error: string | null
}

const initialState: DevicesState = {
  userDevices: {},
  loading: {},
  fetched: {},
  error: null,
}

// Async thunks
export const fetchUserDevices = createAsyncThunk(
  'devices/fetchUserDevices',
  async (userId: string, { rejectWithValue }) => {
    const result = await apiCall(
      AdminUsersService.adminUsersControllerGetUserDevicesV1,
      rejectWithValue,
      userId
    )
    const response = result as { devices?: DeviceDetail[] } | undefined
    return { userId, devices: response?.devices || [] }
  }
)

// Slice
const devicesSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
  },
  extraReducers: builder => {
    // Fetch user devices
    builder
      .addCase(fetchUserDevices.pending, (state, action) => {
        state.loading[action.meta.arg] = true
        state.error = null
      })
      .addCase(fetchUserDevices.fulfilled, (state, action) => {
        const { userId, devices } = action.payload
        state.loading[userId] = false
        state.fetched[userId] = true
        state.userDevices[userId] = devices
      })
      .addCase(fetchUserDevices.rejected, (state, action) => {
        const userId = action.meta.arg
        state.loading[userId] = false
        state.fetched[userId] = true // Mark as fetched even on error to prevent infinite retry
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch device information'
      })
  },
})

export const { clearError } = devicesSlice.actions
export const devicesReducer = devicesSlice.reducer

// Base selectors
const selectDevicesState = (state: { devices: DevicesState }) => state.devices
const selectUserDevicesMap = (state: { devices: DevicesState }) => state.devices.userDevices
const selectDevicesLoadingMap = (state: { devices: DevicesState }) => state.devices.loading
const selectDevicesFetchedMap = (state: { devices: DevicesState }) => state.devices.fetched

// Memoized selectors
export const selectUserDevices = (userId: string) =>
  createSelector([selectUserDevicesMap], userDevicesMap => userDevicesMap[userId] || [])

export const selectDevicesLoading = (userId: string) =>
  createSelector([selectDevicesLoadingMap], loadingMap => loadingMap[userId] || false)

export const selectDevicesFetched = (userId: string) =>
  createSelector([selectDevicesFetchedMap], fetchedMap => fetchedMap[userId] || false)

export const selectDevicesError = createSelector(
  [selectDevicesState],
  devicesState => devicesState.error
)
